import React from 'react';

import { format } from 'date-fns';

import Translate from 'src/app_deprecated/components/form/layout/Translate.react';

import { StateAndProvinceDropdown } from 'src/app/components/form-elements/dropdowns/state-province-dropdown';
import { DatePicker } from 'src/app/components/lib/date-picker';
import { InputAdornment } from 'src/app/components/lib/input';
import { MenuItem } from 'src/app/components/lib/menu-item';
import { CustomerTypeDropdown } from 'src/app/pages/customers/customers/segments/details/segment-query-builder/form-elements/customer-types-multiselect';
import { GenderOptionDropdown } from 'src/app/pages/customers/customers/segments/details/segment-query-builder/form-elements/gender-options-multiselect';
import { ProductCategoryDropdown } from 'src/app/pages/customers/customers/segments/details/segment-query-builder/form-elements/product-category-multiselect';

import { useNewSegmentEnabled } from '../../utils';

import { MonthOptionDropdown } from './form-elements/birthday-months-multiselect';
import { BrandDropdown } from './form-elements/brand-multiselect';
import { CustomerGroupDropdown } from './form-elements/customer-groups-multiselect';
import { LastPurchaseLocationDropdown } from './form-elements/last-purchase-location-multiselect';
import { LoyaltyTierDropdown } from './form-elements/loyalty-tier-multiselect';
import { PaymentOptionDropdown } from './form-elements/payment-options-select';
import { TagDropdown } from './form-elements/tag-multiselect';
import { getOperatorsFromOperatorGroup, ValueEnum } from './operators';
import { Value, LabelContainer, BottomAlignedLabel, StyledSelect, StyledInput } from './query-builder.styles';
import { CustomerQueryBuilderUnit, type Variant } from './schema';

import type { OperatorGroup } from './operators';

type OperatorInputProps = {
  disabled?: boolean;
  index: number;
  onOperatorChange: (operator: string) => void;
  onValueChange: (value: string, secondValue?: boolean) => void;
  onVariantChange: (variant: string) => void;
  operator?: string;
  operatorGroup: OperatorGroup;
  secondValue?: string | null;
  unit?: CustomerQueryBuilderUnit;
  value: string;
  variant?: string | null;
  variants: Variant[];
};

export function OperatorInput({
  disabled,
  index,
  onOperatorChange,
  onValueChange,
  onVariantChange,
  operator,
  operatorGroup,
  secondValue,
  unit,
  value,
  variant = '',
  variants,
}: OperatorInputProps) {
  // keep the naming of this generic so that we only need to update the flag in the method that uses it
  const enableNewSegmentSchema = useNewSegmentEnabled();
  const operators = getOperatorsFromOperatorGroup(enableNewSegmentSchema, operatorGroup);

  const endInputAdornment = (unit: CustomerQueryBuilderUnit | undefined) => {
    const unitMap = {
      [CustomerQueryBuilderUnit.day]: 'days',
      [CustomerQueryBuilderUnit.quantity]: 'qty',
      [CustomerQueryBuilderUnit.year]: 'years',
      [CustomerQueryBuilderUnit.percent]: '%',
    };

    const label = unit ? unitMap[unit] : undefined;

    return label ? <InputAdornment position='end'>{label}</InputAdornment> : null;
  };

  const startInputAdornment = (unit: CustomerQueryBuilderUnit | undefined) => {
    const unitMap = {
      [CustomerQueryBuilderUnit.dollar]: '$',
    };

    const label = unit ? unitMap[unit] : undefined;

    return label ? <InputAdornment position='start'>{label}</InputAdornment> : null;
  };

  const renderInput = (operator) => {
    if (!operator) {
      return null;
    }

    const startAdornment = startInputAdornment(unit);
    const endAdornment = endInputAdornment(unit);

    switch (operator.valueType) {
      case ValueEnum.Boolean:
        return (
          <StyledSelect
            disabled={disabled}
            paddingRightInput='42px'
            placeholder='select a value'
            sx={{ minWidth: '100px' }}
            value={value}
            onChange={({ target }) => onValueChange(target.value)}
          >
            <MenuItem value='1'>true</MenuItem>
            <MenuItem value='0'>false</MenuItem>
          </StyledSelect>
        );
      case ValueEnum.LoyaltyTier:
        return (
          <LoyaltyTierDropdown
            disabled={disabled}
            values={value}
            onChange={(loyaltyTierOptions) => {
              onValueChange(String(loyaltyTierOptions));
            }}
          />
        );
      case ValueEnum.Date:
        return (
          <DatePicker
            allowPastDate
            date={value}
            disableClearDate
            disabled={disabled}
            key='date'
            placeholder='select a date'
            onDateChange={(newDate) => {
              const formattedDate = format(newDate, 'yyyy-MM-dd');
              onValueChange(formattedDate);
            }}
          />
        );
      case ValueEnum.Percent:
        return (
          <>
            <StyledInput
              disabled={disabled}
              endAdornment={endAdornment}
              type='number'
              value={value}
              onChange={({ target }) => onValueChange(target.value)}
            />
            {variants?.length > 0 && (
              <StyledSelect
                disabled={disabled}
                paddingRightInput='42px'
                placeholder='select a value'
                value={variant ?? ''}
                onChange={({ target }) => onVariantChange(target.value)}
              >
                {variants?.map(({ id, label }) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </StyledSelect>
            )}
          </>
        );
      case ValueEnum.Number:
        return (
          <>
            <StyledInput
              disabled={disabled}
              endAdornment={endAdornment}
              startAdornment={startAdornment}
              type='number'
              value={value}
              onChange={({ target }) => onValueChange(String(target.value))}
            />
            {variants?.length > 0 && (
              <StyledSelect
                disabled={disabled}
                paddingRightInput='42px'
                placeholder='select a value'
                value={variant ?? ''}
                onChange={({ target }) => onVariantChange(String(target.value))}
              >
                {variants?.map(({ id, label }) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </StyledSelect>
            )}
          </>
        );
      case ValueEnum.NumberRange:
        return (
          <>
            <StyledInput
              disabled={disabled}
              endAdornment={endAdornment}
              key='number-range-start'
              startAdornment={startAdornment}
              type='number'
              value={value}
              onChange={({ target }) => onValueChange(String(target.value))}
            />
            <LabelContainer minWidth='14px'>
              <BottomAlignedLabel>
                <Translate>to</Translate>
              </BottomAlignedLabel>
            </LabelContainer>
            <StyledInput
              disabled={disabled}
              endAdornment={endAdornment}
              key='number-range-end'
              startAdornment={startAdornment}
              type='number'
              value={secondValue ?? ''}
              onChange={({ target }) => onValueChange(String(target.value), true)}
            />
            {variants?.length > 0 && (
              <StyledSelect
                disabled={disabled}
                paddingRightInput='42px'
                placeholder='select a value'
                value={variant ?? ''}
                onChange={({ target }) => onVariantChange(target.value)}
              >
                {variants?.map(({ id, label }) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </StyledSelect>
            )}
          </>
        );
      case ValueEnum.DateRange:
        return (
          <>
            <DatePicker
              allowPastDate
              date={value}
              disableClearDate
              disabled={disabled}
              key='date-range-start'
              placeholder='select a date'
              onDateChange={(newDate) => {
                const formattedDate = format(newDate, 'yyyy-MM-dd');
                onValueChange(formattedDate);
              }}
            />
            <LabelContainer minWidth='22px'>
              <BottomAlignedLabel>
                <Translate>and</Translate>
              </BottomAlignedLabel>
            </LabelContainer>
            <DatePicker
              allowPastDate
              date={secondValue ?? ''}
              disableClearDate
              disabled={disabled}
              key='date-range-end'
              placeholder='select a date'
              onDateChange={(newDate) => {
                const formattedDate = format(newDate, 'yyyy-MM-dd');
                onValueChange(formattedDate, true);
              }}
            />
          </>
        );
      case ValueEnum.StateLocation:
        return (
          <StateAndProvinceDropdown
            disabled={disabled}
            placeholder='select a state/province'
            sx={{ minWidth: '300px' }}
            value={value}
            onChange={(state) => {
              onValueChange(state);
            }}
          />
        );
      case ValueEnum.ProductCategory:
        return (
          <>
            <ProductCategoryDropdown
              disabled={disabled}
              values={value}
              onChange={(categoryOptions) => {
                onValueChange(String(categoryOptions));
              }}
            />
            {variants?.length > 0 && (
              <StyledSelect
                disabled={disabled}
                paddingRightInput='42px'
                placeholder='select a value'
                value={variant ?? ''}
                onChange={({ target }) => onVariantChange(target.value)}
              >
                {variants?.map(({ id, label }) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </StyledSelect>
            )}
          </>
        );
      case ValueEnum.Brand:
        return (
          <>
            <BrandDropdown
              disabled={disabled}
              values={value}
              onChange={(brandOptions) => {
                onValueChange(String(brandOptions));
              }}
            />
            {variants?.length > 0 && (
              <StyledSelect
                disabled={disabled}
                paddingRightInput='42px'
                placeholder='select a value'
                value={variant ?? ''}
                onChange={({ target }) => onVariantChange(target.value)}
              >
                {variants?.map(({ id, label }) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </StyledSelect>
            )}
          </>
        );
      case ValueEnum.Tag:
        return (
          <>
            <TagDropdown
              disabled={disabled}
              values={value}
              onChange={(tagOptions) => {
                onValueChange(String(tagOptions));
              }}
            />
            {variants?.length > 0 && (
              <StyledSelect
                disabled={disabled}
                paddingRightInput='42px'
                placeholder='select a value'
                value={variant ?? ''}
                onChange={({ target }) => onVariantChange(target.value)}
              >
                {variants?.map(({ id, label }) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </StyledSelect>
            )}
          </>
        );
      case ValueEnum.CustomerType:
        return (
          <CustomerTypeDropdown
            automationId={`query-builder_customer-type-multi-select-dropdown-${String(index)}`}
            disabled={disabled}
            values={value}
            onChange={(customerTypeOptions) => {
              onValueChange(String(customerTypeOptions));
            }}
          />
        );
      case ValueEnum.CustomerGroup:
        return (
          <CustomerGroupDropdown
            automationId={`query-builder_customer-group-multi-select-dropdown-${String(index)}`}
            disabled={disabled}
            values={value}
            onChange={(customerGroupOptions) => {
              onValueChange(String(customerGroupOptions));
            }}
          />
        );
      case ValueEnum.Gender:
        return (
          <GenderOptionDropdown
            disabled={disabled}
            values={value}
            onChange={(genderOptions) => {
              onValueChange(String(genderOptions));
            }}
          />
        );
      case ValueEnum.PaymentMethod:
        return enableNewSegmentSchema.paymentMethodOptionsAttribute ? (
          <>
            <PaymentOptionDropdown
              disabled={disabled}
              values={value}
              onChange={(paymentOptions) => {
                onValueChange(String(paymentOptions));
              }}
            />
            {variants?.length > 0 && (
              <StyledSelect
                disabled={disabled}
                paddingRightInput='42px'
                placeholder='select a value'
                value={variant ?? ''}
                onChange={({ target }) => onVariantChange(target.value)}
              >
                {variants?.map(({ id, label }) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </StyledSelect>
            )}
          </>
        ) : null;
      case ValueEnum.BirthdayMonth:
        return (
          <MonthOptionDropdown
            disabled={disabled}
            values={value}
            onChange={(monthOptions) => {
              onValueChange(String(monthOptions));
            }}
          />
        );
      case ValueEnum.LastPurchaseLocation:
        return (
          <LastPurchaseLocationDropdown
            disabled={disabled}
            values={value}
            onChange={(locationOptions) => {
              onValueChange(String(locationOptions));
            }}
          />
        );
      case ValueEnum.String:
      default:
        return (
          <StyledInput
            disabled={disabled}
            key='string'
            type='text'
            value={value}
            onChange={({ target }) => onValueChange(target.value)}
          />
        );
    }
  };

  const selectedOperator = operators.find((op) => op.name === operator);

  return (
    <>
      <StyledSelect
        automationId={`query-builder_select-operator_select-${String(index)}`}
        disabled={disabled || selectedOperator?.valueType === ValueEnum.Boolean}
        paddingRightInput='42px'
        placeholder='select an option'
        value={operator ?? ''}
        onChange={({ target }) => onOperatorChange(target.value)}
      >
        {operators.map((operator) => (
          <MenuItem key={operator.name} value={operator.name}>
            {operator.label}
          </MenuItem>
        ))}
      </StyledSelect>
      {operator && <Value>{renderInput(selectedOperator)}</Value>}
    </>
  );
}
